import { Trans, useLingui } from "@lingui/react/macro";
import { TriangleAlert } from "lucide-react";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";

import {
  type CreateDefaultSymptomSchema,
  SupportedSymptom,
  apiPostHfSymptoms,
  apiPostSymptoms,
} from "../../../api";
import { markReadsStale } from "../../../api/query-client";
import { AppModel } from "../../../app/app-model";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { useSupportedSymptoms } from "../../../catalogs/supported-symptoms";
import { Dialog, DialogBody } from "../../../components/ui-kit/dialog";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  CollectModeProps,
  RecordDialogActions,
  RecordDialogTitle,
  ADD_DIALOG_CLASS,
  ADD_DIALOG_WIDTH,
} from "../../../components/ui-kit/record-dialog-chrome";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { getNamesValues } from "../../../utils/data-utils";
import { getServerNowISO } from "../../../utils/server-time";
import { createRowId, maybeCapitalizeFirstLetter } from "../../../utils/utils";

/** The record this dialog builds, for callers using collect mode. */
export type SymptomPayload = ReturnType<typeof toSymptomPayload>;

export const AddSymptomsDialog = observer(function AddSymptomsDialog({
  app,
  onClose,
  onSaved,
  onCollect,
  patientId,
  docked,
  recordedAt,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
} & CollectModeProps<SymptomPayload>) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      width={ADD_DIALOG_WIDTH}
      className={ADD_DIALOG_CLASS}
      docked={docked}
    >
      <AddSymptomsBody
        app={app}
        onClose={onClose}
        onSaved={onSaved}
        onCollect={onCollect}
        patientId={patientId}
        docked={docked}
        recordedAt={recordedAt}
      />
    </Dialog>
  );
});

const AddSymptomsBody = observer(function AddSymptomsBody({
  app,
  onClose,
  onSaved,
  onCollect,
  patientId,
  docked,
  recordedAt,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
} & CollectModeProps<SymptomPayload>) {
  const recordPatientId = patientId ?? app.requirePatient().patientId;
  const supportedSymptoms = useSupportedSymptoms();

  const createRow = useCallback(
    () => createSymptomRow(supportedSymptoms[0]?.data_name ?? "", recordedAt),
    [supportedSymptoms, recordedAt],
  );

  // The catalog is derived synchronously, so seed one row per symptom up front
  // (computed once) instead of populating via an effect after the first render.
  const [initialRows] = useState(() =>
    supportedSymptoms.map((s) => createSymptomRow(s.data_name, recordedAt)),
  );
  const accordion = useRowAccordion(createRow, initialRows);

  // `POST /hf/symptoms` writes the study pair in one request and rejects a
  // partial one, so the form has to offer both rows or neither. Collect mode
  // never reaches that endpoint — the ingest writes each record on the
  // per-symptom contract — so half the pair is fine there.
  const hfNames = accordion.rows.map((r) => r.symptom_name).filter(isHfSymptom);
  const hfPairBroken =
    !onCollect &&
    hfNames.length > 0 &&
    !HF_SYMPTOM_NAMES.every((n) => hfNames.includes(n));

  const hasValidRows =
    accordion.rows.length > 0 &&
    !hfPairBroken &&
    accordion.rows.every(isSymptomRowComplete);

  async function save() {
    // Collect mode: the extraction reviewer builds records to ingest later
    // with the rest of a document's approved entities, so this dialog hands
    // them back instead of writing them now. Same form, same validation.
    if (onCollect) {
      onCollect(
        accordion.rows.map((row) => toSymptomPayload(row, recordPatientId)),
      );
      onClose();
      return;
    }

    const hfRows = accordion.rows.filter((r) => isHfSymptom(r.symptom_name));
    const generalRows = accordion.rows.filter(
      (r) => !isHfSymptom(r.symptom_name),
    );

    // Last attempt's reasons go before this one starts: the study-pair step
    // below returns early on failure, and a row left carrying an old reason
    // would read as this attempt's verdict on a row it never reached.
    for (const row of generalRows) {
      row.__saveError = null;
    }

    // Study pair first: it is one all-or-nothing request, and nothing else has
    // been sent yet, so a failure leaves the whole form intact to retry from.
    if (hfRows.length > 0) {
      try {
        await apiPostHfSymptoms({
          body: {
            number_of_datapoints: hfRows.length,
            symptoms: hfRows.map((row) =>
              toSymptomPayload(row, recordPatientId),
            ),
          },
        });
      } catch (error) {
        window.handleError(error);
        return;
      }
    }

    // The generic endpoint takes one symptom per request, so each row fails on
    // its own. The rows already written stay written — they are independent
    // records — and only the ones that failed are kept, carrying their reason.
    const failedRows: SymptomRow[] = [];
    for (const row of generalRows) {
      try {
        await apiPostSymptoms({ body: toSymptomPayload(row, recordPatientId) });
      } catch (error) {
        maybeReportError(error);
        row.__saveError = error;
        failedRows.push(row);
      }
    }

    // Nothing to refresh when every row bounced and there was no pair to write.
    if (hfRows.length > 0 || failedRows.length < generalRows.length) {
      markReadsStale();
      onSaved();
    }

    if (failedRows.length > 0) {
      accordion.initRows(failedRows);
      return;
    }
    onClose();
  }

  return (
    <>
      <RecordDialogTitle>
        <Trans>Add symptoms</Trans>
      </RecordDialogTitle>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <DialogBody className="overflow-x-hidden">
          <RowAccordion
            {...accordion}
            summaryLabel={(row) => <RowSummary row={row} />}
            isRowComplete={docked ? isSymptomRowComplete : undefined}
            keepHeight={docked}
            renderRow={(row) => (
              <RowForm row={row} supportedSymptoms={supportedSymptoms} />
            )}
          />
        </DialogBody>

        {hfPairBroken && (
          <ErrorMessage
            message={
              <Trans>
                Shortness of breath and fatigue score are recorded together —
                keep a row for both, or remove both.
              </Trans>
            }
          />
        )}

        <RecordDialogActions
          disabled={!hasValidRows}
          started={docked ? accordion.startedRowCount : undefined}
        >
          <Trans>Add</Trans>
        </RecordDialogActions>
      </Form>
    </>
  );
});

/**
 * A row's collapsed header. Its own component so that it observes the row:
 * only the expanded row renders the reason its save failed, and the rest would
 * otherwise sit in the list looking like they went through.
 */
const RowSummary = observer(function RowSummary({ row }: { row: SymptomRow }) {
  const { t } = useLingui();
  return (
    <>
      {row.__saveError != null && (
        <TriangleAlert
          size={14}
          className="text-danger-foreground shrink-0"
          aria-label={t`Not saved`}
        />
      )}
      {maybeCapitalizeFirstLetter(row.symptom_name)}
      {row.symptom_value != null && (
        <span className="text-muted-foreground">— {row.symptom_value}</span>
      )}
    </>
  );
});

const RowForm = observer(function RowForm({
  row,
  supportedSymptoms,
}: {
  row: SymptomRow;
  supportedSymptoms: SupportedSymptom[];
}) {
  const { t } = useLingui();
  return (
    <>
      <FormTable dataAdd className="max-w-full">
        <FormRow
          label={t`Symptom`}
          data={
            <Select
              model={row}
              attr="symptom_name"
              values={getNamesValues(supportedSymptoms)}
            />
          }
          mandatory
        />
        <FormRow
          label={t`Symptom datetime`}
          data={
            <DateInput
              model={row}
              attr="symptom_datetime"
              type="datetime-local"
            />
          }
          mandatory
        />
        <FormRow
          label={t`Value`}
          data={<NumberInput model={row} attr="symptom_value" />}
          mandatory
        />
        <FormRow
          label={t`Change in last 6 months?`}
          data={
            <Checkbox
              model={row}
              attr="symptom_change_in_last_six_m"
              label={t`Yes`}
            />
          }
        />
        {row.symptom_change_in_last_six_m && (
          <>
            <FormRow
              label={t`Change date`}
              data={
                <DateInput model={row} attr="symptom_change_date" type="date" />
              }
            />
            <FormRow
              label={t`Value before`}
              data={<NumberInput model={row} attr="symptom_value_before" />}
            />
          </>
        )}
        <FormRow
          label={t`Comment`}
          data={
            <TextInput
              model={row}
              attr="comment"
              multiline="autosize"
              width={330}
            />
          }
        />
      </FormTable>
      {/* This row's own save failed while others went through; it stayed in
          the form so it can be retried. */}
      <ErrorMessage
        message={row.__saveError ? errorText(row.__saveError) : ""}
      />
    </>
  );
});

/**
 * The HF-validation study's symptoms. `POST /hf/symptoms` accepts only these
 * two and requires both in one request (see /api/v1/hf/symptoms docs); every
 * other catalog symptom is written one at a time through `POST /symptoms`.
 */
const HF_SYMPTOM_NAMES = ["shortness of breath", "fatigue score"];

function isHfSymptom(symptomName: string) {
  return HF_SYMPTOM_NAMES.includes(symptomName);
}

type SymptomRow = {
  __rowId: string;
  /** Set when this row alone failed to save, so the form can show the reason. */
  __saveError: unknown;
  symptom_datetime: string;
  symptom_name: string;
  symptom_value: number | null;
  symptom_change_in_last_six_m: boolean;
  symptom_change_date: string | null;
  symptom_value_before: number | null;
  comment: string;
};

function isSymptomRowComplete(r: SymptomRow): boolean {
  return !!r.symptom_name && !!r.symptom_datetime && r.symptom_value != null;
}

function createSymptomRow(
  symptomName: string,
  recordedAt?: string,
): SymptomRow {
  return observable({
    __rowId: createRowId(),
    __saveError: null,
    symptom_datetime: recordedAt ?? getServerNowISO(),
    symptom_name: symptomName,
    symptom_value: null,
    symptom_change_in_last_six_m: false,
    symptom_change_date: null,
    symptom_value_before: null,
    comment: "",
  });
}

function toSymptomPayload(
  row: SymptomRow,
  patientId: number,
): CreateDefaultSymptomSchema {
  if (row.symptom_value == null) {
    throw new Error("symptom_value is required");
  }
  return {
    symptom_name: row.symptom_name,
    symptom_datetime: row.symptom_datetime,
    symptom_value: row.symptom_value,
    symptom_change_in_last_six_m: row.symptom_change_in_last_six_m,
    symptom_change_date: row.symptom_change_date,
    symptom_value_before: row.symptom_value_before,
    // Empty comment is sent as null — the schema marks it optional/nullable.
    comment: row.comment || null,
    patient_id: patientId,
  };
}
